import { useEffect, useState } from "react";
import { AlertTriangle, Archive, CheckCircle2, ClipboardList, Download, ExternalLink, FileArchive, FileText, Link2, LockKeyhole, Pencil, Plus, Power, RotateCcw, Search, TableProperties, Trash2, X } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { api, useAuth } from "@/auth/AuthContext";
import { AgreementSubmissionSummary } from "@/pages/AgreementSubmissionSummary";
import { AgreementTargetManager } from "@/components/AgreementTargetManager";
import { RevisionHistory } from "@/components/RevisionHistory";
import { getTaskDisplayName } from "@/utils/taskDisplay";
import { formatCalendarDate } from "@/utils/dateDisplay";

const STATUS_META = {
  belum_dikerjakan: { label: "Belum Dikerjakan", tone: "chip-tone-warn" },
  dalam_proses_verifikasi: { label: "Dalam Proses Verifikasi", tone: "chip-tone-info" },
  selesai: { label: "Selesai", tone: "chip-tone-success" },
  perlu_perbaikan: { label: "Perlu Perbaikan", tone: "chip-tone-danger" },
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
}

async function downloadApiFile(path, fallbackName) {
  const response = await api.get(path, { responseType: "blob" });
  const disposition = response.headers["content-disposition"] || "";
  const match = disposition.match(/filename="?([^";]+)"?/i);
  const url = URL.createObjectURL(response.data);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = match?.[1] || fallbackName;
  document.body.appendChild(anchor);
  anchor.click(); anchor.remove(); URL.revokeObjectURL(url);
}

export default function ManajemenTask() {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [detail, setDetail] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    try {
      const r = await api.get("/tasks");
      setTasks(r.data);
    } catch (e) { setError(e.response?.data?.detail || "Gagal memuat task."); }
  };
  useEffect(() => { load(); }, []);

  const openDetail = async (task) => {
    setSelectedTask(task);
    setDetail(null);
    try {
      const r = await api.get(`/tasks/${task.task_id}`);
      setDetail(r.data);
    } catch (e) { setError(e.response?.data?.detail || "Gagal memuat detail."); }
  };

  const refreshDetail = async () => {
    if (!selectedTask) return;
    const r = await api.get(`/tasks/${selectedTask.task_id}`);
    setDetail(r.data);
    load();
  };

  const archiveTask = async (task) => {
    setError("");
    try {
      await api.post(`/tasks/${task.task_id}/archive`);
      setNotice(`Task "${getTaskDisplayName(task)}" berhasil diarsipkan.`);
      await load();
    } catch (e) { setError(e.response?.data?.detail || "Gagal mengarsipkan Task."); }
  };

  return (
    <div className="acct-wrap" data-testid="manajemen-task-page">
      <div className="acct-head">
        <div>
          <div className="eyebrow">MANAJEMEN TASK</div>
          <h2>Task Pengelolaan Perguruan Tinggi</h2>
          <p className="muted-text" style={{ fontSize: 12, marginTop: 4 }}>Login sebagai <strong>{user?.display_name}</strong> · {user?.role}</p>
        </div>
        <div className="acct-actions">
          <Link to="/kipk/manajemen-task/arsip" className="download-button ghost" data-testid="task-archive-page-link"><Archive size={14} /> Arsip Task</Link>
          <button className="download-button" data-testid="task-create-btn" onClick={() => setShowCreate(true)}><Plus size={14} /> Tambahkan Task</button>
        </div>
      </div>

      {notice && <div className="info-banner" data-testid="task-notice">✓ {notice}</div>}
      {error && <div className="error-banner" data-testid="task-error"><AlertTriangle size={14} /> {error}</div>}

      <div className="table-wrap">
        <table data-testid="task-table">
          <thead>
            <tr>
              <th>Nama Task</th>
              <th>Jenis</th>
              <th>Deadline</th>
              <th>Statistik</th>
              <th style={{ width: 220 }}>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {tasks.map((t) => (
              <tr key={t.task_id} data-testid={`task-row-${t.task_id}`}>
                <td><button className="task-name-link" data-testid={`task-edit-${t.task_id}`} onClick={() => setEditingTask(t)}><Pencil size={13} /> {getTaskDisplayName(t)}</button><div className="muted-text" style={{ fontSize: 11 }}>{(t.required_docs || []).length} dokumen persyaratan{t.is_closed ? " · Task ditutup" : ""}</div></td>
                <td><span className={`folder-chip ${t.jenis === "umum" ? "chip-tone-info" : "chip-tone-warn"}`} style={{ pointerEvents: "none" }}>{t.jenis === "umum" ? "Umum" : "Khusus"}</span></td>
                <td className="mono" data-testid={`task-deadline-${t.task_id}`}>{formatCalendarDate(t.deadline)}</td>
                <td className="mono" style={{ fontSize: 11 }}>
                  {t.stats?.done || 0}/{t.stats?.assigned || 0} selesai · {t.stats?.pending || 0} menunggu
                </td>
                <td className="task-row-actions">
                  {t.is_closed && <span className="folder-chip chip-tone-danger" data-testid={`task-closed-${t.task_id}`}><LockKeyhole size={11} /> Ditutup</span>}
                  <button className="folder-chip" data-testid={`task-open-${t.task_id}`} onClick={() => openDetail(t)}>Detail</button>
                  {t.is_closed && <button className="folder-chip task-archive-action" data-testid={`task-archive-${t.task_id}`} onClick={() => archiveTask(t)}><Archive size={11} /> Arsipkan</button>}
                </td>
              </tr>
            ))}
            {tasks.length === 0 && <tr><td colSpan={5} className="empty-state">Belum ada task. Klik "Tambahkan Task" untuk membuat.</td></tr>}
          </tbody>
        </table>
      </div>

      {showCreate && <TaskCreateDialog onClose={() => setShowCreate(false)} onCreated={(t) => { setShowCreate(false); setNotice(`Task "${t.nama}" berhasil dibuat.`); load(); }} />}
      {editingTask && <TaskCreateDialog task={editingTask} structureLocked={(editingTask.stats?.submitted || 0) > 0} onClose={() => setEditingTask(null)} onCreated={(t) => { setEditingTask(null); setNotice(`Task "${t.nama}" berhasil diperbarui.`); load(); }} onClosed={(t) => { setEditingTask(null); setNotice(`Task "${t.nama}" berhasil ditutup.`); load(); }} />}
      {selectedTask && detail && <TaskDetailDrawer detail={detail} onClose={() => { setSelectedTask(null); setDetail(null); }} onChanged={refreshDetail} />}
    </div>
  );
}

// ============== Create Task Dialog ==============
function TaskCreateDialog({ task = null, structureLocked = false, onClose, onCreated, onClosed }) {
  const navigate = useNavigate();
  const isEditing = Boolean(task);
  const isAgreement = task?.regular_task_key === "agreement";
  const [jenis, setJenis] = useState(task?.jenis || "umum");
  const [nama, setNama] = useState(task?.nama || "");
  const [ann, setAnn] = useState(task?.announcement_link || "");
  const [additionalInformation, setAdditionalInformation] = useState(task?.additional_information || "");
  const [annStatus, setAnnStatus] = useState({ ok: null, msg: "", checking: false });
  const [deadline, setDeadline] = useState(task?.deadline?.slice(0, 10) || "");
  const [docs, setDocs] = useState(() => (task?.document_requirements || (task?.required_docs || []).map((name) => ({ doc_id: name, name, input_type: "link" }))));
  const [templates, setTemplates] = useState(() => task?.task_templates || []);
  const [studentUpdateEnabled, setStudentUpdateEnabled] = useState(Boolean(task?.student_update_enabled));
  const [studentFields, setStudentFields] = useState(() => (task?.student_update_fields || []).map((field) => ({ ...field, options: [...(field.options || [])] })));
  const [ptQuery, setPtQuery] = useState("");
  const [ptResults, setPtResults] = useState([]);
  const [selectedPts, setSelectedPts] = useState(task?.pt_targets || []); // [{pt_id,namapt,kodept}]
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);

  const searchPt = async (q) => {
    setPtQuery(q);
    if (jenis !== "khusus") return;
    try {
      const r = await api.get("/pt-search", { params: { q, limit: 30 } });
      setPtResults(r.data);
    } catch { /* ignore */ }
  };
  useEffect(() => { if (jenis === "khusus") searchPt(""); /* eslint-disable-next-line */ }, [jenis]);

  const checkAnn = async () => {
    if (!ann.trim()) return;
    setAnnStatus({ ok: null, msg: "", checking: true });
    try {
      const r = await api.post("/util/check-url", { url: ann.trim() });
      setAnnStatus({ ok: r.data.ok, msg: r.data.ok ? "Tautan publik OK" : r.data.reason, checking: false });
    } catch { setAnnStatus({ ok: false, msg: "Gagal cek", checking: false }); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    const cleanDocs = docs.map((item) => ({ doc_id: item.doc_id, name: item.name.trim(), input_type: item.input_type })).filter((item) => item.name);
    if (jenis === "khusus" && selectedPts.length === 0 && !isAgreement) { setError("Pilih minimal satu PT untuk task khusus."); return; }
    if (!deadline) { setError("Deadline wajib diisi."); return; }
    setBusy(true);
    try {
      const payload = {
        jenis, nama: nama.trim(), announcement_link: ann.trim(), additional_information: additionalInformation.trim(), deadline,
        required_docs: [], document_requirements: cleanDocs,
        task_templates: templates.map((item, index) => ({ template_id: item.template_id, label: item.label.trim() || `Isi Template ${index + 1}`, url: item.url.trim() })).filter((item) => item.url),
        student_update_enabled: studentUpdateEnabled,
        student_update_fields: studentFields.map((item) => ({ ...item, label: item.label.trim(), options: item.field_type === "choice" ? item.options.map((value) => value.trim()).filter(Boolean) : [] })).filter((item) => item.label),
        pt_ids: jenis === "khusus" ? selectedPts.map((p) => p.pt_id) : [],
      };
      const r = isEditing ? await api.put(`/tasks/${task.task_id}`, payload) : await api.post("/tasks", payload);
      onCreated(r.data);
    } catch (e) { setError(e.response?.data?.detail || "Gagal membuat task."); }
    finally { setBusy(false); }
  };

  const togglePt = (p) => setSelectedPts((s) => s.find((x) => x.pt_id === p.pt_id) ? s.filter((x) => x.pt_id !== p.pt_id) : [...s, p]);

  const closeEditedTask = async () => {
    if (!isEditing || task.is_closed) return;
    setBusy(true); setError("");
    try {
      const response = await api.post(`/tasks/${task.task_id}/close`);
      onClosed(response.data);
    } catch (e) { setError(e.response?.data?.detail || "Gagal menutup Task."); }
    finally { setBusy(false); }
  };

  return (
    <div className="drawer-backdrop" data-testid={isEditing ? "task-edit-backdrop" : "task-create-backdrop"} onClick={onClose}>
      <div className="detail-drawer" data-testid={isEditing ? "task-edit-dialog" : "task-create-dialog"} style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
        <div className="drawer-top">
          <div><div className="eyebrow"><ClipboardList size={12} /> {isEditing ? "EDIT TASK" : "TASK BARU"}</div><h2 data-testid={isEditing ? "task-edit-title" : "task-create-title"}>{isEditing ? getTaskDisplayName(task) : "Tambahkan Task"}</h2></div>
          <button className="icon-button" aria-label="Tutup" data-testid={isEditing ? "task-edit-close" : "task-create-close"} onClick={onClose}><X size={18} /></button>
        </div>
        {structureLocked && <div className="info-banner" data-testid="task-structure-locked"><LockKeyhole size={14} /> Jenis Task, sasaran PT, dan dokumen dikunci karena sudah ada submission. Update Data Mahasiswa tetap dapat diubah sepenuhnya; perubahan kolom/pilihan dapat membersihkan nilai lama yang tidak lagi sesuai.</div>}
        {error && <div className="error-banner" data-testid="task-create-error"><AlertTriangle size={14} /> {error}</div>}
        <form className="acct-form" onSubmit={submit} data-testid="task-create-form">
          <label>
            <span>Jenis Task</span>
            <div className="task-type-options">
              <button type="button" className={`tab-button ${jenis === "umum" ? "active" : ""}`} data-testid="task-jenis-umum" disabled={structureLocked || isAgreement} onClick={() => setJenis("umum")}>Umum (semua PT)</button>
              <button type="button" className={`tab-button ${jenis === "khusus" ? "active" : ""}`} data-testid="task-jenis-khusus" disabled={structureLocked || isAgreement} onClick={() => setJenis("khusus")}>Khusus</button>
              {!isEditing && <button type="button" className="tab-button task-regular-type" data-testid="task-jenis-reguler" onClick={() => { onClose(); navigate("/kipk/manajemen-task/reguler"); }}>Task Reguler</button>}
            </div>
          </label>
          <label><span>Nama Task</span><input data-testid="task-nama" value={nama} onChange={(e) => setNama(e.target.value)} required disabled={isAgreement} /></label>
          <label>
            <span>Tautan Surat Pengumuman/Pemberitahuan</span>
            <div style={{ display: "flex", gap: 8 }}>
              <input data-testid="task-ann" style={{ flex: 1 }} value={ann} onChange={(e) => { setAnn(e.target.value); setAnnStatus({ ok: null, msg: "", checking: false }); }} placeholder="Opsional · https://..." />
              <button type="button" className="download-button ghost" data-testid="task-ann-check" onClick={checkAnn} disabled={annStatus.checking || !ann.trim()}>{annStatus.checking ? "Cek…" : "Periksa"}</button>
            </div>
            {annStatus.ok === true && <span style={{ color: "var(--green)", fontSize: 11, marginTop: 4 }}>✓ {annStatus.msg}</span>}
            {annStatus.ok === false && <span style={{ color: "var(--red)", fontSize: 11, marginTop: 4 }}>✗ {annStatus.msg}</span>}
          </label>
          <label><span>Keterangan</span><textarea rows={3} maxLength={5000} data-testid="task-additional-information" value={additionalInformation} onChange={(event) => setAdditionalInformation(event.target.value)} placeholder="Informasi tambahan bagi Perguruan Tinggi terkait Task ini…" /><small className="agreement-field-help">{additionalInformation.length}/5000 karakter</small></label>
          <label>
            <span>Unggah Template (Tautan File yang Dapat Diunduh)</span>
            {templates.map((item, index) => <div key={index} className="task-builder-row">
              <input data-testid={`task-template-label-${index}`} value={item.label} onChange={(e) => setTemplates((current) => current.map((value, idx) => idx === index ? { ...value, label: e.target.value } : value))} placeholder={`Isi Template ${index + 1}`} />
              <input data-testid={`task-template-url-${index}`} value={item.url} onChange={(e) => setTemplates((current) => current.map((value, idx) => idx === index ? { ...value, url: e.target.value } : value))} placeholder="https://..." />
              <button type="button" className="icon-button" data-testid={`task-template-remove-${index}`} onClick={() => setTemplates((current) => current.filter((_, idx) => idx !== index))}><Trash2 size={14} /></button>
            </div>)}
            <button type="button" className="download-button ghost task-add-control" data-testid="task-add-template" onClick={() => setTemplates((current) => [...current, { label: `Isi Template ${current.length + 1}`, url: "" }])}><Link2 size={12} /> Tambah Template</button>
          </label>
          <label><span>Deadline Task</span><input type="date" data-testid="task-deadline" value={deadline} onChange={(e) => setDeadline(e.target.value)} required /></label>
          <label>
            <span>Dokumen yang Perlu Diunggah</span>
            {docs.map((doc, i) => (
              <div key={i} className="task-builder-row">
                <input data-testid={`task-doc-${i}`} disabled={structureLocked || isAgreement} value={doc.name} placeholder={`Nama dokumen ${i + 1}`} onChange={(e) => setDocs((current) => current.map((value, idx) => idx === i ? { ...value, name: e.target.value } : value))} />
                <select data-testid={`task-doc-type-${i}`} disabled={structureLocked || isAgreement} value={doc.input_type} onChange={(e) => setDocs((current) => current.map((value, idx) => idx === i ? { ...value, input_type: e.target.value } : value))}><option value="link">Tautan Dokumen</option><option value="file">Unggah File</option></select>
              <button type="button" className="icon-button" data-testid={`task-doc-remove-${i}`} disabled={structureLocked || isAgreement} onClick={() => setDocs((current) => current.filter((_, idx) => idx !== i))}><Trash2 size={14} /></button>
              </div>
            ))}
            <button type="button" className="download-button ghost task-add-control" data-testid="task-add-doc" disabled={structureLocked || isAgreement} onClick={() => setDocs((current) => [...current, { name: "", input_type: "link" }])}><Plus size={12} /> Tambah Dokumen</button>
          </label>

          <div className="task-student-builder">
            <label className="task-feature-toggle"><input type="checkbox" data-testid="task-student-enable" checked={studentUpdateEnabled} onChange={(e) => { setStudentUpdateEnabled(e.target.checked); if (e.target.checked && !studentFields.length) setStudentFields([{ label: "", field_type: "text", options: [], required: false }]); }} /><span><TableProperties size={15} /> Update Data Mahasiswa</span></label>
            {studentUpdateEnabled && <div data-testid="task-student-fields">
              <p className="muted-text">Tentukan kolom tambahan yang akan diisi PT untuk masing-masing mahasiswa.</p>
              {studentFields.map((field, index) => <div key={index} className="task-student-field-row">
                <input data-testid={`task-student-label-${index}`} value={field.label} onChange={(e) => setStudentFields((current) => current.map((value, idx) => idx === index ? { ...value, label: e.target.value } : value))} placeholder={`Nama kolom ${index + 1}`} />
                <select data-testid={`task-student-type-${index}`} value={field.field_type} onChange={(e) => setStudentFields((current) => current.map((value, idx) => idx === index ? { ...value, field_type: e.target.value, options: e.target.value === "choice" && !value.options.length ? ["", ""] : value.options } : value))}><option value="text">Teks bebas</option><option value="choice">Pilihan jawaban</option></select>
                <label className="task-required-toggle"><input type="checkbox" data-testid={`task-student-required-${index}`} checked={field.required} onChange={(e) => setStudentFields((current) => current.map((value, idx) => idx === index ? { ...value, required: e.target.checked } : value))} /> Wajib</label>
                <button type="button" className="icon-button" data-testid={`task-student-remove-${index}`} onClick={() => setStudentFields((current) => current.filter((_, idx) => idx !== index))}><Trash2 size={14} /></button>
                {field.field_type === "choice" && <div className="task-choice-options" data-testid={`task-student-options-${index}`}><div className="task-choice-options-head"><span>Pilihan Jawaban</span><span className="task-testid-alias" data-testid={index === 0 ? "task-student-option-add" : undefined}><button type="button" className="download-button ghost" data-testid={`task-student-option-add-${index}`} onClick={() => setStudentFields((current) => current.map((value, idx) => idx === index ? { ...value, options: [...value.options, ""] } : value))}><Plus size={12} /> Tambah Pilihan</button></span></div>{field.options.map((option, optionIndex) => <div key={optionIndex} className="task-choice-option-row" data-testid={index === 0 && optionIndex === 0 ? "task-student-option" : undefined}><input data-testid={`task-student-option-${index}-${optionIndex}`} value={option} onChange={(e) => setStudentFields((current) => current.map((value, idx) => idx === index ? { ...value, options: value.options.map((currentOption, currentIndex) => currentIndex === optionIndex ? e.target.value : currentOption) } : value))} placeholder={`Pilihan ${optionIndex + 1}`} /><span className="task-testid-alias" data-testid={index === 0 && optionIndex === 0 ? "task-student-option-remove" : undefined}><button type="button" className="icon-button" aria-label={`Hapus pilihan ${optionIndex + 1}`} data-testid={`task-student-option-remove-${index}-${optionIndex}`} onClick={() => setStudentFields((current) => current.map((value, idx) => idx === index ? { ...value, options: value.options.filter((_, currentIndex) => currentIndex !== optionIndex) } : value))}><Trash2 size={13} /></button></span></div>)}</div>}
              </div>)}
              <button type="button" className="download-button ghost task-add-control" data-testid="task-add-student-field" onClick={() => setStudentFields((current) => [...current, { label: "", field_type: "text", options: [], required: false }])}><Plus size={12} /> Tambah Kolom Mahasiswa</button>
            </div>}
          </div>

          {jenis === "khusus" && !isAgreement && (
            <label>
              <span>Pilih Perguruan Tinggi ({selectedPts.length} dipilih)</span>
              <div className="search-wrap" style={{ marginBottom: 8 }}><Search size={13} /><input data-testid="task-pt-search" disabled={structureLocked || isAgreement} placeholder="Cari kode/nama PT…" value={ptQuery} onChange={(e) => searchPt(e.target.value)} /></div>
              {selectedPts.length > 0 && (
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                  {selectedPts.map((p) => (
                    <span key={p.pt_id} className="folder-chip chip-tone-info" style={{ pointerEvents: structureLocked || isAgreement ? "none" : "auto", cursor: structureLocked || isAgreement ? "default" : "pointer" }} onClick={() => !structureLocked && !isAgreement && togglePt(p)} data-testid={`task-pt-chip-${p.pt_id}`}>
                      {p.namapt} <X size={11} />
                    </span>
                  ))}
                </div>
              )}
              <div style={{ maxHeight: 200, overflow: "auto", border: "1px solid var(--line)", borderRadius: 10 }}>
                {ptResults.map((p) => {
                  const sel = selectedPts.some((x) => x.pt_id === p.pt_id);
                  return (
                    <button type="button" key={p.pt_id} disabled={structureLocked || isAgreement} data-testid={`task-pt-option-${p.pt_id}`} onClick={() => togglePt(p)} style={{ width: "100%", textAlign: "left", padding: "10px 14px", background: sel ? "var(--blue-soft)" : "transparent", border: 0, borderBottom: "1px solid var(--line)", cursor: "pointer", fontSize: 13 }}>
                      <strong>{p.namapt}</strong>
                      <div className="muted-text mono" style={{ fontSize: 11 }}>{p.kodept}</div>
                    </button>
                  );
                })}
                {ptResults.length === 0 && <div className="empty-state" style={{ padding: 16 }}>Tidak ada hasil.</div>}
              </div>
            </label>
          )}
          {isAgreement && <AgreementTargetManager taskId={task.task_id} onTargetsChanged={setSelectedPts} />}

          {confirmClose && <div className="draft-confirm" data-testid="task-edit-close-confirmation"><AlertTriangle size={18} /><div><strong>Tutup Task ini?</strong><p>Perguruan tinggi yang belum menyelesaikan Task tidak akan dapat mengelola atau mengirimkan Task ini. Perubahan form yang belum disimpan tidak akan ikut tersimpan.</p><div className="draft-confirm-actions"><button type="button" className="download-button ghost" data-testid="task-edit-close-cancel" onClick={() => setConfirmClose(false)}>Batal</button><button type="button" className="download-button task-close-button" data-testid="task-edit-close-confirm" disabled={busy} onClick={closeEditedTask}>{busy ? "Menutup…" : "Ya, Tutup Task"}</button></div></div></div>}
          <div className="task-form-actions">
            <button type="submit" className="download-button task-save-button" data-testid={isEditing ? "task-edit-submit" : "task-create-submit"} disabled={busy}>{busy ? "Menyimpan…" : isEditing ? "Simpan Perubahan" : "Buat Task"}</button>
            {isEditing && !task.is_closed && <button type="button" className="download-button task-close-button" data-testid="task-edit-close-task" disabled={busy} onClick={() => setConfirmClose(true)}><LockKeyhole size={13} /> Tutup Task</button>}
            <button type="button" className="download-button ghost" data-testid={isEditing ? "task-edit-cancel" : "task-create-cancel"} onClick={onClose}>Batal</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============== Task Detail Drawer (Admin) ==============
export function TaskDetailDrawer({ detail, onClose, onChanged }) {
  const { task, submissions } = detail;
  const [selectedSub, setSelectedSub] = useState(null);
  const [rejectNotes, setRejectNotes] = useState("");
  const [outOfOrderWarn, setOutOfOrderWarn] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmClose, setConfirmClose] = useState(false);
  const requirements = task.document_requirements || (task.required_docs || []).map((name) => ({ doc_id: name, name, input_type: "link" }));
  const requirementMap = Object.fromEntries(requirements.map((item) => [item.doc_id, item]));

  const download = async (path, name) => {
    setError("");
    try { await downloadApiFile(path, name); }
    catch (e) { setError(e.response?.data?.detail || "File tidak dapat diunduh."); }
  };
  const changeTaskState = async (closed) => {
    setBusy(true); setError("");
    try {
      await api.post(`/tasks/${task.task_id}/${closed ? "close" : "open"}`);
      setConfirmClose(false); await onChanged();
    } catch (e) { setError(e.response?.data?.detail || "Status Task tidak dapat diubah."); }
    finally { setBusy(false); }
  };

  const doVerify = async (ignore = false) => {
    if (!selectedSub) return;
    setBusy(true); setError("");
    try {
      await api.post(`/tasks/${task.task_id}/submissions/${selectedSub.pt_id}/verify`, null, { params: { ignore_order: ignore } });
      setOutOfOrderWarn(null); setSelectedSub(null); onChanged();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === "out_of_order") {
        setOutOfOrderWarn(d);
      } else {
        setError(typeof d === "string" ? d : "Gagal verifikasi.");
      }
    } finally { setBusy(false); }
  };
  const doReject = async () => {
    if (!selectedSub || !rejectNotes.trim()) { setError("Catatan wajib diisi."); return; }
    setBusy(true); setError("");
    try {
      await api.post(`/tasks/${task.task_id}/submissions/${selectedSub.pt_id}/reject`, { revision_notes: rejectNotes.trim() });
      setSelectedSub(null); setRejectNotes(""); onChanged();
    } catch (e) { setError(e.response?.data?.detail || "Gagal menolak."); }
    finally { setBusy(false); }
  };
  const doReopen = async () => {
    if (!selectedSub) return;
    if (!rejectNotes.trim()) { setError("Isi catatan untuk membuka kembali."); return; }
    setBusy(true); setError("");
    try {
      await api.post(`/tasks/${task.task_id}/submissions/${selectedSub.pt_id}/reopen`, { revision_notes: rejectNotes.trim() });
      setSelectedSub(null); setRejectNotes(""); onChanged();
    } catch (e) { setError(e.response?.data?.detail || "Gagal reopen."); }
    finally { setBusy(false); }
  };

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <div className="detail-drawer" data-testid="task-detail-drawer" style={{ maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
        <div className="drawer-top">
          <div>
            <div className="eyebrow"><ClipboardList size={12} /> DETAIL TASK</div>
            <h2 data-testid="task-detail-title">{getTaskDisplayName(task)}</h2>
            <p className="muted-text" style={{ fontSize: 12, marginTop: 4 }}>
              Deadline: <span className="mono" data-testid="task-detail-deadline">{formatCalendarDate(task.deadline)}</span> · Jenis: {task.jenis === "umum" ? "Umum" : "Khusus"}
            </p>
          </div>
          <button className="icon-button" aria-label="Tutup detail" data-testid="task-detail-close" onClick={onClose}><X size={18} /></button>
        </div>

        {task.is_closed && <div className="error-banner" data-testid="task-closed-banner"><LockKeyhole size={14} /> Task ditutup sejak {fmtDateTime(task.closed_at)}. PT yang belum selesai tidak dapat mengisi.</div>}

        {task.announcement_link && (
          <a href={task.announcement_link} target="_blank" rel="noreferrer" className="drawer-cta" data-testid="task-open-announce">
            <FileText size={14} /> Buka Pengumuman <ExternalLink size={13} />
          </a>
        )}
        {(task.task_templates || []).length > 0 && (
          <div className="task-detail-assets" data-testid="task-detail-templates">
            <div className="eyebrow">TEMPLATE TASK</div>
            <div className="task-template-links">{task.task_templates.map((template) => <a key={template.template_id || template.url} href={template.url} target="_blank" rel="noreferrer" className="folder-chip chip-tone-info"><Download size={11} /> {template.label}</a>)}</div>
          </div>
        )}
        {task.regular_task_key === "agreement" && <div className="task-detail-assets" data-testid="pks-admin-task-meta"><div className="eyebrow">PERJANJIAN KERJA SAMA</div><p className="muted-text">Tahun {task.agreement_year} · {task.pt_ids?.length || 0} PT ditargetkan melalui Excel.</p><button className="download-button ghost" data-testid="pks-admin-source-download" onClick={() => download(`/regular-tasks/agreement/${task.task_id}/source`, `Detail_Perjanjian_${task.agreement_year}.xlsx`)}><Download size={13} /> Unduh Excel Detail Perjanjian</button></div>}
        {requirements.length > 0 && (
          <div className="task-detail-assets" data-testid="task-detail-requirements">
            <div className="eyebrow">PERSYARATAN DOKUMEN</div>
            <div className="task-template-links">{requirements.map((item) => <span key={item.doc_id} className="folder-chip"><span>{item.input_type === "file" ? "FILE" : "LINK"}</span> {item.name}</span>)}</div>
          </div>
        )}
        {task.student_update_enabled && (
          <div className="task-detail-assets" data-testid="task-detail-student-update">
            <div className="eyebrow">UPDATE DATA MAHASISWA</div>
            <p className="muted-text">{task.student_update_fields?.length || 0} kolom ditetapkan untuk setiap mahasiswa.</p>
            <div className="task-template-links"><button className="download-button ghost" data-testid="task-student-export-all-combined" onClick={() => download(`/tasks/${task.task_id}/student-updates/export?mode=combined`, "Update_Mahasiswa_Semua_PT.xlsx")}><Download size={13} /> Semua PT · Satu Excel</button><button className="download-button ghost" data-testid="task-student-export-all-separate" onClick={() => download(`/tasks/${task.task_id}/student-updates/export?mode=separate`, "Update_Mahasiswa_Per_PT.zip")}><FileArchive size={13} /> Semua PT · File per PT</button></div>
          </div>
        )}

        <div className="eyebrow" style={{ marginBottom: 8 }}>SUBMISSIONS ({submissions.length} PT)</div>
        <div className="table-wrap" style={{ borderRadius: 12 }}>
          <table>
            <thead><tr><th>#</th><th>Perguruan Tinggi</th><th>Status</th><th>Submit</th><th>Skor</th><th></th></tr></thead>
            <tbody>
              {submissions.map((s, idx) => {
                const meta = STATUS_META[s.status];
                return (
                  <tr key={s.pt_id} data-testid={`task-sub-row-${s.pt_id}`}>
                    <td className="mono">{s.submit_order || "—"}</td>
                    <td><strong>{s.namapt}</strong><div className="muted-text" style={{ fontSize: 11 }}>{s.kodept}</div></td>
                    <td><span className={`folder-chip ${meta.tone}`} style={{ pointerEvents: "none", fontSize: 10 }}>{meta.label}</span></td>
                    <td className="mono" style={{ fontSize: 11 }}>{s.submitted_at ? fmtDateTime(s.submitted_at) : "—"}</td>
                    <td className="mono">{s.score != null ? s.score.toFixed(1) : "—"}</td>
                    <td>
                      {s.status !== "belum_dikerjakan" && (
                        <button className="folder-chip" data-testid={`task-sub-open-${s.pt_id}`} onClick={() => { setSelectedSub(s); setRejectNotes(""); setError(""); setOutOfOrderWarn(null); }}>Buka</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {selectedSub && (
          <div style={{ marginTop: 22, padding: 16, background: "var(--surface-2)", borderRadius: 12 }} data-testid="task-sub-panel">
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <strong>{selectedSub.namapt}</strong>
                <div className="muted-text" style={{ fontSize: 11 }}>Submit {fmtDateTime(selectedSub.submitted_at)}</div>
              </div>
              <span className={`folder-chip ${STATUS_META[selectedSub.status].tone}`} style={{ pointerEvents: "none" }}>{STATUS_META[selectedSub.status].label}</span>
            </div>
            <RevisionHistory items={selectedSub.revision_history || []} testIdPrefix={`admin-revision-${selectedSub.pt_id}`} />
            <div style={{ marginTop: 12 }}>
              <div className="eyebrow">DOKUMEN DIKIRIM</div>
              <div className="task-template-links">
                {Object.entries(selectedSub.documents || {}).map(([docId, value]) => {
                  const requirement = requirementMap[docId] || { name: docId };
                  return typeof value === "object" && value?.kind === "file" ? (
                    <button key={docId} className="folder-chip chip-tone-info" data-testid={`task-file-download-${value.file_id}`} onClick={() => download(`/task-files/${value.file_id}/download`, value.original_filename)}><Download size={11} /> {requirement.name}</button>
                  ) : (
                    <a key={docId} href={value} target="_blank" rel="noreferrer" className="folder-chip chip-tone-info" data-testid={`task-doc-link-${docId}`}><ExternalLink size={11} /> {requirement.name}</a>
                  );
                })}
                {Object.values(selectedSub.documents || {}).some((value) => typeof value === "object" && value?.kind === "file") && <button className="download-button ghost" data-testid="task-files-download-all" onClick={() => download(`/tasks/${task.task_id}/submissions/${selectedSub.pt_id}/files.zip`, `${selectedSub.kodept}_Dokumen.zip`)}><FileArchive size={13} /> Unduh Semua File</button>}
              </div>
            </div>
            {task.student_update_enabled && <button className="download-button ghost task-single-export" data-testid="task-student-export-pt" onClick={() => download(`/tasks/${task.task_id}/student-updates/export?mode=combined&pt_id=${encodeURIComponent(selectedSub.pt_id)}`, `${selectedSub.kodept}_Update_Mahasiswa.xlsx`)}><Download size={13} /> Unduh Data Mahasiswa PT Ini</button>}
            {task.regular_task_key === "agreement" && <AgreementSubmissionSummary taskId={task.task_id} ptId={selectedSub.pt_id} />}

            {outOfOrderWarn && (
              <div className="error-banner" style={{ marginTop: 12 }} data-testid="task-out-of-order-warn">
                <AlertTriangle size={14} /> {outOfOrderWarn.message}
                <div className="muted-text" style={{ fontSize: 11, marginTop: 4 }}>
                  Lebih dulu submit: <strong>{outOfOrderWarn.earlier_pt?.namapt}</strong> ({fmtDateTime(outOfOrderWarn.earlier_pt?.submitted_at)})
                </div>
                <div style={{ marginTop: 8, display: "flex", gap: 6 }}>
                  <button type="button" className="download-button" data-testid="task-verify-ignore-order" onClick={() => doVerify(true)} disabled={busy}>
                    Tetap Verifikasi
                  </button>
                  <button type="button" className="download-button ghost" onClick={() => setOutOfOrderWarn(null)}>Batal</button>
                </div>
              </div>
            )}

            {error && <div className="error-banner" style={{ marginTop: 12 }} data-testid="task-sub-error"><AlertTriangle size={14} /> {error}</div>}

            <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap" }}>
              {selectedSub.status === "dalam_proses_verifikasi" && (
                <button className="download-button" data-testid="task-verify" onClick={() => doVerify(false)} disabled={busy} style={{ background: "var(--green)", borderColor: "var(--green)", boxShadow: "0 4px 12px rgba(16,185,129,.28)" }}>
                  <CheckCircle2 size={13} /> Verifikasi Selesai
                </button>
              )}
              {selectedSub.status === "selesai" && (
                <button className="download-button ghost" data-testid="task-submission-reopen" onClick={doReopen} disabled={busy || !rejectNotes.trim()}>
                  <RotateCcw size={13} /> Buka Kembali (Perlu Perbaikan)
                </button>
              )}
            </div>

            <textarea data-testid="task-reject-notes" rows={3} placeholder="Catatan perbaikan…" value={rejectNotes} onChange={(e) => setRejectNotes(e.target.value)} style={{ width: "100%", marginTop: 12, border: "1px solid var(--line)", borderRadius: 10, padding: "10px 14px", font: "13px var(--font-body)", background: "#fff", resize: "vertical" }} />
            <button className="download-button" data-testid="task-reject" onClick={doReject} disabled={busy || !rejectNotes.trim() || selectedSub.status !== "dalam_proses_verifikasi"} style={{ marginTop: 8, background: "var(--red)", borderColor: "var(--red)" }}>
              <AlertTriangle size={13} /> Kembalikan untuk Diperbaiki
            </button>
          </div>
        )}
        <div className="task-detail-footer-actions" data-testid="task-detail-footer-actions">
          {confirmClose && <div className="draft-confirm" data-testid="task-close-confirmation"><AlertTriangle size={18} /><div><strong>Tutup Task ini?</strong><p>Perguruan tinggi yang belum menyelesaikan Task tidak akan dapat mengisi atau mengirimkan Task setelah ditutup.</p><div className="draft-confirm-actions"><button className="download-button ghost" data-testid="task-close-cancel" onClick={() => setConfirmClose(false)}>Batal</button><button className="download-button task-close-button" data-testid="task-close-confirm" disabled={busy} onClick={() => changeTaskState(true)}>{busy ? "Menutup…" : "Ya, TUTUP TASK"}</button></div></div></div>}
          <div className="task-form-actions">
            {task.is_closed ? <button className="download-button ghost" data-testid="task-detail-reopen" disabled={busy} onClick={() => changeTaskState(false)}><Power size={13} /> Buka Kembali</button> : <button className="download-button task-close-button" data-testid="task-close" disabled={busy} onClick={() => setConfirmClose(true)}><LockKeyhole size={13} /> TUTUP TASK</button>}
          </div>
        </div>
      </div>
    </div>
  );
}
